"use client"

import * as SliderPrimitive from "@radix-ui/react-slider"
import * as React from "react"

import { cn } from "../../utils"

interface SliderProps extends React.ComponentProps<typeof SliderPrimitive.Root> {
	/** 각 thumb에 적용할 접근성 이름. 현재 단일 thumb 사용처만 있어 모든 thumb에 동일하게 적용된다 */
	ariaLabel?: string
	/** 각 thumb의 값을 사람이 읽는 형태로 전달 (예: "50%", "1.2초") */
	ariaValueText?: string
}

function Slider({ className, defaultValue, value, min = 0, max = 100, ariaLabel, ariaValueText, ...props }: SliderProps) {
	const values = React.useMemo(() => (Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max]), [value, defaultValue, min, max])

	return (
		<SliderPrimitive.Root
			data-slot="slider"
			defaultValue={defaultValue}
			value={value}
			min={min}
			max={max}
			className={cn(
				"relative flex w-full touch-none items-center select-none data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
				className
			)}
			{...props}
		>
			<SliderPrimitive.Track
				data-slot="slider-track"
				className={cn(
					"bg-black/20 relative grow overflow-hidden rounded-full",
					"dark:bg-white/20",
					"data-disabled:bg-g200 dark:data-disabled:bg-g200",
					"data-[orientation=horizontal]:h-1 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1"
				)}
			>
				<SliderPrimitive.Range
					data-slot="slider-range"
					className={cn("bg-p500 absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full data-disabled:bg-g400")}
				/>
			</SliderPrimitive.Track>
			{Array.from({ length: values.length }, (_, index) => (
				<SliderPrimitive.Thumb
					data-slot="slider-thumb"
					key={index}
					data-value={values[index]}
					aria-label={ariaLabel}
					aria-valuetext={ariaValueText}
					className={cn(
						"bg-white block border border-g100 ring-ring/15 size-4 shrink-0 rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.2)] cursor-pointer transition-[color,box-shadow]",
						"relative hover:ring-5 focus:ring-8 focus:outline-hidden",
						"after:content-[attr(data-value)] after:absolute after:-top-6 after:left-1/2 after:-translate-x-1/2 after:px-2 after:min-w-6 after:h-5 after:flex after:items-center after:justify-center after:rounded-full after:bg-g200 after:border after:border-border after:text-text after:text-xs after:font-medium after:opacity-0 after:transition-opacity after:pointer-events-none",
						"hover:after:opacity-100",
						"data-disabled:pointer-events-none data-disabled:bg-g400 data-disabled:border-g400 data-disabled:shadow-none data-disabled:after:bg-g400"
					)}
				/>
			))}
		</SliderPrimitive.Root>
	)
}

export { Slider }
